/**
 * Renewal Request Modal Component
 * TASK-361: Document Management & Compliance System
 *
 * Modal for marking a document as renewed with a new expiry date.
 * Extracted from ExpiringDocumentsList for single responsibility.
 */

import React, { useState } from 'react';
import { RefreshCw, Loader2 } from 'lucide-react';
import { Button } from '../ui/button';
import { ExpiringDocument, getDocTypeLabel } from '../../types/documentCompliance';

interface RenewalRequestModalProps {
  document: ExpiringDocument;
  isLoading: boolean;
  onSubmit: (newExpiryDate: string, notes?: string) => Promise<void>;
  onClose: () => void;
}

const RenewalRequestModal: React.FC<RenewalRequestModalProps> = ({
  document: doc,
  isLoading,
  onSubmit,
  onClose,
}) => {
  const [newExpiryDate, setNewExpiryDate] = useState('');
  const [renewNotes, setRenewNotes] = useState('');

  const handleSubmit = async () => {
    if (!newExpiryDate) return;
    await onSubmit(newExpiryDate, renewNotes || undefined);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div
        className="bg-white rounded-lg shadow-xl w-full max-w-md p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="text-lg font-semibold text-gray-900 mb-4">
          Mark Document as Renewed
        </h3>

        <div className="space-y-4">
          {/* Document Info */}
          <div className="bg-gray-50 rounded-lg p-3">
            <p className="font-medium text-gray-900">{doc.filename}</p>
            <p className="text-sm text-gray-500">
              {doc.client_name} &bull; {getDocTypeLabel(doc.doc_type)}
            </p>
          </div>

          {/* New Expiry Date */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              New Expiration Date <span className="text-red-500">*</span>
            </label>
            <input
              type="date"
              value={newExpiryDate}
              onChange={(e) => setNewExpiryDate(e.target.value)}
              min={new Date().toISOString().split('T')[0]}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#F7B500] focus:border-transparent"
            />
          </div>

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Notes <span className="text-gray-400">(optional)</span>
            </label>
            <textarea
              value={renewNotes}
              onChange={(e) => setRenewNotes(e.target.value)}
              placeholder="Add any notes about this renewal..."
              rows={2}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm resize-none focus:outline-none focus:ring-2 focus:ring-[#F7B500] focus:border-transparent"
            />
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-2">
            <Button
              variant="outline"
              onClick={onClose}
              disabled={isLoading}
              className="flex-1"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={!newExpiryDate || isLoading}
              className="flex-1 flex items-center justify-center gap-1.5"
            >
              {isLoading ? (
                <Loader2 size={14} className="animate-spin" />
              ) : (
                <RefreshCw size={14} />
              )}
              Mark Renewed
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RenewalRequestModal;
